import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import IconSearchOrange from '../assets/images/common/icon-search-orange.svg';

import { SearchItems } from '../types/api/SearchApiType';
import { SuggestItems, SUGGEST_ITEMS_INIT } from '../types/common/SearchResultType';
import { ErrorMessages } from '../types/common/Messages';

import { HighlightedSearchText } from '../components/HighlightedSearchText';
import { ErrorModalModal } from '../components/ErrorModal';
import { searchApi } from '../api/search';
import { searchNormaliz } from '../utils/stringUtil';
import { setState as searchWordSetState } from '../slice/SearchWordSlice';
import { setState as setSearchResultOffset } from '../slice/SearchResultOffset';
import { useAppDispatch, useAppSelector } from '../store/hooks';

type SearchTextFieldProps = {
    inputClassName: string; // テキストフィールドのクラス名
    buttonClassName: string; // ボタンのクラス名
};

// 検索TOP
export const SearchTextField: React.FC<SearchTextFieldProps> = ({ inputClassName, buttonClassName }) => {
    const navigation = useNavigate();
    const dispatch = useAppDispatch();
    // dispatchされたサジェスト用の検索一覧結果を取得
    const products = useAppSelector((s) => s.Conditions).items;
    // dispatchされた検索キーワードを取得
    const searchWord = useAppSelector((s) => s.SearchWord);
    //
    const insideRef = useRef<HTMLDivElement>(null);

    // 検索キーワード　初期値 store値
    const [searchText, setSearchText] = useState<string>(searchWord);
    // 検索状態の管理
    const [isSearch, setIsSearch] = useState<boolean>(false);
    // サジェストリスト管理
    const [suggestList, setSggestList] = useState<Array<SuggestItems>>([SUGGEST_ITEMS_INIT]);
    // ハイライトされたサジェストリスト管理(表示用)
    const [highlightedSuggestList, setHighlightedSuggestList] = useState<Array<JSX.Element>>([]);
    // サジェスト開閉管理
    const [openSuggest, setOpenSuggest] = useState<boolean>(false);
    // サジェストのフォーカスindex管理
    const [forcusIndex, setForcusIndex] = useState<number>(0);

    // APIエラー発生時のエラーモーダル管理
    const [errorModalMessages, setErrorModalMessages] = useState<Array<string>>([]);
    const [openErrorlModal, setOpenErrorModal] = useState<boolean>(false);

    const SPACE = '　';

    // dispatchの表示遅延考慮
    useEffect(() => {
        setSearchText(searchWord);
    }, [searchWord]);

    // サジェスト動作
    useEffect(() => {
        if (searchText.length > 0) {
            // 検索条件の正規化
            const suggestText = searchNormaliz(searchText);
            // サジェスト用のテキストで絞り込み
            // 機種名で絞り込み（型番が無いもので絞り込み）
            const suggestProdName = products
                .filter(
                    (row: SearchItems) =>
                        row.mp_product_name_search.includes(suggestText) && row.mp_model_number1_search === null
                )
                .slice(0, 10)
                .map((row) => {
                    return { ...row, model_flg: 0 };
                });

            // 型番で絞り込み
            const suggestModelNumber = products
                .filter((row: SearchItems) =>
                    (row.mp_model_number1_search === null || undefined ? '' : row.mp_model_number1_search).includes(
                        suggestText
                    )
                )
                .slice(0, 10)
                .map((row) => {
                    return { ...row, model_flg: 1 };
                });

            // 合算のうち上位10件のみ表示
            const suggestResult = [...suggestProdName, ...suggestModelNumber].slice(0, 10);

            // 検索文言強調
            const editText = suggestResult.map((row, index) => {
                return (
                    <React.Fragment key={`suggestText-row-${index}`}>
                        <HighlightedSearchText
                            searchWord={searchText}
                            showName={row.mp_product_name}
                            showNameSearch={row.mp_product_name_search}
                        />
                        {row.mp_model_number1 && (
                            <span className='kataban'>
                                <HighlightedSearchText
                                    searchWord={searchText}
                                    showName={row.mp_model_number1}
                                    showNameSearch={row.mp_model_number1_search}
                                />
                            </span>
                        )}
                    </React.Fragment>
                );
            });
            setHighlightedSuggestList(editText);
            setSggestList(suggestResult);
            setOpenSuggest(true);
        } else {
            // 初期化
            setSggestList([]);
        }
    }, [searchText]);

    useEffect(() => {
        const el = insideRef.current;
        // サジェスト開閉管理
        const hundleClickOutside = (e: MouseEvent) => {
            if (!el?.contains(e?.target as Node)) {
                // 自分自身からフォーカスが移った場合、サジェストを閉じる
                setOpenSuggest(false);
            } else {
                setOpenSuggest(true);
            }
        };
        if (el) {
            document.addEventListener('click', hundleClickOutside);
        }

        // リスナーの削除
        return () => {
            document.removeEventListener('click', hundleClickOutside);
        };
    }, [insideRef]);

    useEffect(() => {
        // 画面遷移後はサジェストを閉じる
        setOpenSuggest(false);
    }, [navigation]);

    // 検索ボタン押下時 検索API実行
    const textSearch = () => {
        // テキストで検索
        const searchApiText = async () => {
            if (!isSearch) {
                setIsSearch(true);
                // API実行
                const searchResult = await searchApi.searchKeyword(dispatch, searchText);
                // 検索が正常終了している場合
                if (searchResult === '') {
                    // 検索キーワードのdispatch
                    dispatch(searchWordSetState(searchText));
                    // 検索オフセットの初期化dispatch
                    dispatch(setSearchResultOffset(0));
                    // サジェストを閉じる
                    setOpenSuggest(false);
                    // 検索結果に遷移
                    navigation('/searchResult');
                } else {
                    setErrorModalMessages([ErrorMessages.API300x_SEARCH_ERROR.replace('%1', searchResult)]);
                    setOpenErrorModal(true);
                }
                setIsSearch(false);
            }
        };

        void searchApiText();
    };

    // Inputエリア内のキー押下
    const pressKeyInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key == 'Enter') {
            setForcusIndex(0);
            void textSearch();
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setForcusIndex(0);
                // サジェストにフォーカスを移動
                document.querySelector<HTMLInputElement>('ul.suggestion-list li#suggest-show-0')?.focus();
                break;
        }
    };

    // サジェスト選択時のテキストフィールド返却
    const setSuggestItem = (index: number) => {
        const selectItem =
            suggestList[index].model_flg === 0
                ? suggestList[index].mp_product_name
                : suggestList[index].mp_model_number1;
        setSearchText(selectItem);
    };

    // サジェスト内のキー押下制御
    const pressKeySuggest = (e: React.KeyboardEvent<HTMLLIElement>, index: number) => {
        // デフォルト動作のキャンセル
        e.preventDefault();
        if (e.key == 'Enter') {
            setSuggestItem(index);
            document.querySelector<HTMLInputElement>('ul.suggestion-list li#suggest-show-0')?.focus();
            setForcusIndex(0);
        }

        switch (e.key) {
            case 'ArrowUp':
                const upIndex = forcusIndex === 0 ? 0 : forcusIndex - 1;
                const moveInput = forcusIndex === 0;
                setForcusIndex(upIndex);
                if (moveInput) {
                    // inputにフォーカスを移動
                    document.querySelector<HTMLInputElement>('input#search')?.focus();
                } else {
                    document.querySelector<HTMLInputElement>(`ul.suggestion-list li#suggest-show-${upIndex}`)?.focus();
                }
                break;
            case 'ArrowDown':
                const downIndex = forcusIndex === 9 ? 9 : forcusIndex + 1;
                setForcusIndex(downIndex);
                document.querySelector<HTMLInputElement>(`ul.suggestion-list li#suggest-show-${downIndex}`)?.focus();
                break;
        }
    };

    return (
        <>
            <div className='field is-grouped search_box' ref={insideRef}>
                <div className='control is-expanded'>
                    <div className='control has-icons-left has-icons-right is-fullwidth'>
                        <input
                            id='search'
                            className={inputClassName}
                            type='text'
                            autoComplete='off'
                            placeholder='機種名、型番を入力してください'
                            name='pattern'
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => {
                                pressKeyInput(e);
                            }}
                        />
                        <>
                            {openSuggest && suggestList.length > 0 && (
                                <ul className='suggestion-list' tabIndex={-1}>
                                    {highlightedSuggestList.map((suggestion, index) => {
                                        return (
                                            <li
                                                id={`suggest-show-${index}`}
                                                key={`suggest-show-${index}`}
                                                onMouseDown={() => {
                                                    setSuggestItem(index);
                                                }}
                                                onKeyDown={(e) => {
                                                    pressKeySuggest(e, index);
                                                }}
                                                tabIndex={0}
                                            >
                                                {suggestList[index].maker_name}
                                                {SPACE}
                                                {suggestion}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </>
                        <span className='icon is-small is-left'>
                            <img src={IconSearchOrange} alt='' />
                        </span>
                    </div>
                </div>
                <div className='control'>
                    <a className={buttonClassName} onClick={textSearch}>
                        検索する
                    </a>
                </div>
            </div>
            <ErrorModalModal
                errorMessages={errorModalMessages}
                openState={openErrorlModal}
                setOpenState={setOpenErrorModal}
            />
        </>
    );
};
